/**
 * sms-encoding — GSM-7 / UCS-2 classification for the SMS body field.
 *
 * WHY THIS FILE EXISTS RATHER THAN AN IMPORT. `smsSegments()` in
 * `@v0y4ge/message-engine-core` is the authority on the segment COUNT, and this
 * file does not compete with it: the tables and the arithmetic below are the
 * engine's own (`packages/message-engine-core/src/render/sms-segments.ts`),
 * copied character-for-character, so the two answer the same number for the same
 * string. Two reasons it is duplicated instead of called:
 *
 *  1. `@v0y4ge/message-engine-core` is NOT a dependency of this app. `app/package.json`
 *     declares api-client, api-contracts, domain-types, jrni-tokens and jrni-ui —
 *     adding a fifth workspace package to the authoring host is a kit decision, not
 *     a screen decision, so it is raised in cc-outbox rather than taken here.
 *  2. The engine's export is `(body: string) => number`. The indicator needs three
 *     more facts that number cannot carry — which encoding the body landed in, how
 *     much of the current segment is left, and WHICH characters forced UCS-2 — and
 *     they all come from the same single pass. Even with the dependency added, the
 *     classifier would still have to exist here.
 *
 * If the engine ever exports the analysis instead of the count, delete this file
 * and import it. The tables are the contract between the two.
 */

/** GSM-7 single-segment capacity, in septets. */
const SEG_GSM = 160;
/** Concatenated GSM-7 segments lose 7 septets to the UDH header. */
const SEG_GSM_CONCAT = 153;
/** UCS-2 halves the payload: 70 UTF-16 code units, 67 when concatenated. */
const SEG_UCS2 = 70;
const SEG_UCS2_CONCAT = 67;

/** The GSM-7 basic alphabet (3GPP TS 23.038). One septet per character. */
const GSM_BASIC = '@£$¥èéùìòÇ\nØø\rÅå'
  + 'Δ_ΦΓΛΩΠΨΣΘΞÆæßÉ'
  + ' !"#¤%&\'()*+,-./0123456789:;<=>?'
  + '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§'
  + '¿abcdefghijklmnopqrstuvwxyzäöñüà';

/** The GSM-7 extension table. Each of these costs TWO septets, not one. */
const GSM_EXTENDED = '^{}\\[~]|€';

export type SmsEncoding = "gsm7" | "ucs2";

export interface SmsAnalysis {
  encoding: SmsEncoding;
  /** Septets in GSM-7, UTF-16 code units in UCS-2 — the unit the capacity is in. */
  units: number;
  /** Plain character count, for the author who is counting what they typed. */
  chars: number;
  segments: number;
  /** Capacity of the segment the body currently ends in. */
  capacity: number;
  /** Units left before a new segment starts. */
  remaining: number;
  /**
   * The characters that forced UCS-2, de-duplicated in first-appearance order.
   * Empty in GSM-7. This is the whole point of the indicator: a typographic
   * quote is invisible next to a straight one, and it is what halves the budget.
   */
  offenders: string[];
}

export function analyzeSms(body: string): SmsAnalysis {
  const text = body || "";
  const chars = [...text].length;
  let septets = 0;
  const offenders: string[] = [];

  for (const ch of text) {
    if (GSM_BASIC.includes(ch)) septets += 1;
    else if (GSM_EXTENDED.includes(ch)) septets += 2;
    else if (!offenders.includes(ch)) offenders.push(ch);
  }

  if (offenders.length > 0) {
    // UCS-2 counts UTF-16 code units, so an astral character (an emoji) is two.
    const units = text.length;
    const segments = units === 0 ? 0 : units <= SEG_UCS2 ? 1 : Math.ceil(units / SEG_UCS2_CONCAT);
    const capacity = segments <= 1 ? SEG_UCS2 : SEG_UCS2_CONCAT;
    return {
      encoding: "ucs2", units, chars, segments, capacity,
      remaining: Math.max(0, segments <= 1 ? SEG_UCS2 - units : segments * SEG_UCS2_CONCAT - units),
      offenders,
    };
  }

  const segments = septets === 0 ? 0 : septets <= SEG_GSM ? 1 : Math.ceil(septets / SEG_GSM_CONCAT);
  const capacity = segments <= 1 ? SEG_GSM : SEG_GSM_CONCAT;
  return {
    encoding: "gsm7", units: septets, chars, segments, capacity,
    remaining: Math.max(0, segments <= 1 ? SEG_GSM - septets : segments * SEG_GSM_CONCAT - septets),
    offenders: [],
  };
}

/** Names the invisible ones, so the warning can say WHICH character to replace. */
const CHAR_NAME: Record<string, string> = {
  "\u2018": "‘ curly apostrophe", "\u2019": "’ curly apostrophe",
  "\u201C": "“ curly quote", "\u201D": "” curly quote",
  "\u201A": "‚ low quote", "\u201E": "„ low quote",
  "\u00AB": "« guillemet", "\u00BB": "» guillemet",
  "\u2013": "– en dash", "\u2014": "— em dash", "\u2026": "… ellipsis",
  "\u00A0": "non-breaking space", "\u202F": "narrow no-break space",
  "\u2011": "‑ non-breaking hyphen", "\u00B4": "´ acute accent",
};

/** ASCII stand-ins that keep the message in GSM-7 — offered, never auto-applied. */
export const GSM_SUBSTITUTES: Record<string, string> = {
  "\u2018": "'", "\u2019": "'", "\u201A": "'", "\u201B": "'",
  "\u201C": '"', "\u201D": '"', "\u201E": '"', "\u00AB": '"', "\u00BB": '"',
  "\u2013": "-", "\u2014": "-", "\u2011": "-", "\u2026": "...",
  "\u00A0": " ", "\u202F": " ", "\u2009": " ", "\u200A": " ",
  "\u00B4": "'", "\u02BC": "'", "\u2032": "'", "\u2033": '"',
};

export const describeChar = (ch: string): string => CHAR_NAME[ch] ?? ch;

/** True when every offender has an ASCII stand-in — i.e. one click fixes the bill. */
export const allFixable = (offenders: string[]): boolean =>
  offenders.length > 0 && offenders.every((ch) => ch in GSM_SUBSTITUTES);

/** Replaces every substitutable character; leaves anything without a stand-in alone. */
export function toGsmSafe(body: string): string {
  return [...(body || "")].map((ch) => GSM_SUBSTITUTES[ch] ?? ch).join("");
}
